import React, { useEffect, useState } from 'react'
import { Button } from 'antd'
import { useTranslate } from '@helpers'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faFileCertificate } from '@fortawesome/pro-solid-svg-icons/faFileCertificate'
import { userProfileService } from '@services'
import { ICertificate } from 'interfaces/certificate'
import CourseType from 'modules/reusables/courseType'
import MandatoryCourse from 'modules/reusables/mandatoryCourse'
import { useNotification } from 'hooks/notifications/useNotification'
import {
  Wrapper,
  Description,
  RankmiTableWrapper,
  DownloadButton
} from './styled'
import EmptyState from 'modules/reusables/emptyState'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { globalReactService } from '@commons'

interface IProps {
  userId: number
}

export const Certificates = ({ userId }: IProps) => {
  const { t } = useTranslate()
  const notification = useNotification()
  const [certificates, setCertificates] = useState<ICertificate[]>([])
  const [selectedCertificates, setSelectedCertificates] = useState<
    ICertificate[]
  >([])
  const [totalCertificates, setTotalCertificates] = useState(0)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [order, setOrder] = useState('ascend')
  const [fieldToOrder, setFieldToOrder] = useState('name')
  const prefix = 'configuration.userProfile.tabs.certificates'
  const translatePrefix = 'configuration.userProfile.tabs.courses'

  useEffect(() => {
    getCertificates(currentPage, searchText, fieldToOrder, order)
  }, [])

  const getCertificates = (
    page: number,
    search: string,
    orderBy: string,
    orderType: string
  ) => {
    setLoading(true)
    userProfileService
      .getMyCertificates(userId, page, 10, search, orderBy, orderType)
      .then((response) => {
        setCertificates(
          response.data.map((certificate: ICertificate) => {
            return {
              ...certificate,
              key: certificate.certificate_id
            }
          })
        )
        setTotalCertificates(response.total)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onSearch = (value: string) => {
    setCurrentPage(1)
    setSearchText(value)
    getCertificates(1, value, fieldToOrder, order)
  }

  const onSortChange = (value: { order: string; field: string }) => {
    let newOrder = ''
    let newField = ''
    switch (value.order) {
      case 'ascend':
        newOrder = 'asc'
        break
      case 'descend':
        newOrder = 'desc'
        break
      default:
        newOrder = 'asc'
        break
    }
    switch (value.field) {
      default:
        newField = value.field
        break
    }
    setOrder(newOrder)
    setFieldToOrder(newField)
    getCertificates(currentPage, searchText, newField, newOrder)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getCertificates(value, searchText, fieldToOrder, order)
  }

  // eslint-disable-next-line no-unused-vars
  const onRowSelected = (value: any) => {
    if (value.dispatcher !== 'handleUnselectAllRowsOnFilter') {
      const certificateList: ICertificate[] = [
        ...selectedCertificates,
        ...value.selectedRows
      ]
      const addedCertificates: number[] = []
      setSelectedCertificates(
        certificateList.filter((certificate) => {
          if (
            value.selectedRowKeys.includes(certificate.certificate_id) &&
            !addedCertificates.includes(certificate.certificate_id)
          ) {
            addedCertificates.push(certificate.certificate_id)
            return true
          }
          return false
        })
      )
    }
  }

  const downloadCertificate = (certificate: ICertificate) => {
    if (certificate.url) {
      window.open(certificate.url)
    } else {
      window.open(
        `${
          globalReactService.lmsApp
        }/download-certificate/user/${userId}/certificates/${
          certificate.certificate_id
        }/courses/${certificate.course_id || 0}`
      )
    }
    notification.success(t(`${prefix}.notifications.download`))
  }

  const downloadSelectedCertificates = () => {
    notification.success(t(`${prefix}.notifications.downloadSelected`))
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`)
    }
  }

  const columns = [
    {
      title: t(`${translatePrefix}.name`),
      render: (data: ICertificate) =>
        data.course_name ? data.course_name : data.name
    },
    {
      title: t(`${prefix}.mandatory.title`),
      sorteable: true,
      dataIndex: 'mandatory',
      key: 'mandatory',
      width: 160,
      render: (mandatory: boolean) => <MandatoryCourse value={mandatory} />
    },
    {
      title: t(`${prefix}.type`),
      sorteable: true,
      dataIndex: 'course_subtype',
      key: 'course_subtype',
      width: 160,
      render: (courseSubtype: number) => <CourseType value={courseSubtype} />
    },
    {
      width: 102,
      align: 'center',
      fixed: 'right',
      render: (data: ICertificate) => (
        <DownloadButton>
          <Button onClick={() => downloadCertificate(data)}>
            {t(`${prefix}.download`)}
          </Button>
        </DownloadButton>
      )
    }
  ]

  // eslint-disable-next-line no-unused-vars
  const actions = [
    {
      label: t(`${prefix}.downloadSelectedCertificates`).replace(
        '{total}',
        `(${selectedCertificates.length})`
      ),
      onClick: downloadSelectedCertificates,
      disabled: selectedCertificates.length === 0
    }
  ]

  return (
    <Wrapper>
      {totalCertificates || loading || searchText ? (
        <div>
          <Description>{t(`${prefix}.header.description`)}</Description>
          <RankmiTableWrapper>
            <RankmiTable
              columnHeaders={columns}
              data={certificates}
              countIcon={faFileCertificate}
              search={searchText}
              onSearch={onSearch}
              total={totalCertificates}
              locale={locale}
              loading={loading}
              currentPage={currentPage}
              onPaginationChange={onPaginationChange}
              initialRowsPerPage={10}
              onSortChange={onSortChange}
              selectedRows={selectedCertificates.map(
                (row) => row.certificate_id
              )}
              // onRowSelected={onRowSelected}
              // actions={actions}
              showScrollButtons
              customEmpty={
                loading ? (
                  <div></div>
                ) : (
                  <EmptyState
                    title={t(`${prefix}.emptyState.noMatches`)}
                    icon={faFileCertificate as IconProp}
                  />
                )
              }
              delaySearch
              responsive
            />
          </RankmiTableWrapper>
        </div>
      ) : (
        <EmptyState
          title={t(`${prefix}.emptyState.noCertificates`)}
          icon={faFileCertificate as IconProp}
        />
      )}
    </Wrapper>
  )
}

export default Certificates
